import {useCallback, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation, Trans} from 'react-i18next';
import {useQueryClient} from '@tanstack/react-query';
import ChekinProSettings from 'ChekinProSettings';
import {BookingSummary, SDK_MODE, useTimeoutRef, QUERY_CACHE_KEYS} from '@chekin/core';
import {usePath} from 'hooks';
import {useAuth} from 'hooks/auth/useAuth';
import {useSummary} from 'context/summary';
import {useReservation} from 'context/reservation';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useIVOnProperty} from 'hooks/useIVOnProperty';
import {useModalControls} from '@chekin/guestapp-ui-kit';
import {StatusModalStates, useCustomLotties} from '@chekin/shared';
import {useShowDealsOnRegistration} from 'components/upselling/useShowDealsOnRegistration';
import {PATHS} from 'routes/constants';
import {ModalGuestRegisteredNextStepContent} from './ModalGuestRegisteredNextStep';
import {GuestsProcessingModal} from './GuestsProcessingModal';
import {SignUpSuggestionModal} from './SignUpSuggestionModal';
import {StyledModalSheet, StyledStatusModal} from './styled';

const TimeoutFinishProcess = 3000;

const getTitle = (state?: StatusModalStates, defaultTitle?: string) => {
  if (state === StatusModalStates.LOADING) {
    return 'sending_your_data';
  } else if (defaultTitle) {
    return defaultTitle;
  } else if (state === StatusModalStates.ERROR) {
    return 'error';
  } else {
    return 'guest_registered';
  }
};

type ModalTypes = {
  open: boolean;
  resetForm?: () => void;
  onClose: () => void;
  name?: string;
  state?: StatusModalStates;
  errorMessage?: string;
  modalTitle?: string;
  isEdition?: boolean;
};

function GuestRegisteringModal({
  open,
  name,
  state,
  onClose,
  errorMessage,
  modalTitle,
  isEdition,
}: ModalTypes) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const title = getTitle(state, modalTitle);
  const timeoutRef = useTimeoutRef();
  const queryClient = useQueryClient();
  const {isLoggedIn} = useAuth();
  const {isInstantCheckInEnabled, data: summary} = useSummary();
  const {data: reservation} = useReservation();
  const layoutMode = useLayoutMode();
  const {shouldShowDeals, markDealsAsShown} = useShowDealsOnRegistration({
    housingId: reservation?.housing_id,
    summary,
    reservationId: reservation?.id,
    enabled: layoutMode === 'default',
  });
  const {isIVOnPropertyUnavailable} = useIVOnProperty();

  const {
    isOpen: isOpenSignUpModal,
    openModal: openSignUpModal,
    closeModal: closeSignUpModal,
  } = useModalControls();

  const {
    isOpen: isOpenNextStepModal,
    openModal: openNextStepModal,
    closeModal: closeNextStepModal,
  } = useModalControls();

  const handleNavigateToCart = useCallback(() => {
    markDealsAsShown();
    navigate(getFullPath(PATHS.payments.main));
  }, [navigate, getFullPath, markDealsAsShown]);

  const finishProcess = useCallback(() => {
    markDealsAsShown();
    const summary = queryClient.getQueryData<BookingSummary>([QUERY_CACHE_KEYS.summary]);
    const isAllRegistered = summary?.guest_registration?.status === 'COMPLETE';
    if (isAllRegistered) {
      ChekinProSettings.events.onAllGuestsRegistered();
      if (layoutMode === 'kiosko') {
        openNextStepModal();
        return;
      }
      navigate(getFullPath(PATHS.home));
      return;
    } else if (ChekinProSettings.mode === SDK_MODE.guestRegistrationOnly) {
      navigate(getFullPath(PATHS.home));
      return;
    }
  }, [
    queryClient,
    navigate,
    getFullPath,
    openNextStepModal,
    markDealsAsShown,
    layoutMode,
  ]);

  const shouldShowSignUpModal = !isLoggedIn && isInstantCheckInEnabled;
  const onFinishProcess = useCallback(() => {
    if (shouldShowSignUpModal) {
      timeoutRef.current = setTimeout(() => {
        openSignUpModal();
      }, TimeoutFinishProcess);
    } else {
      finishProcess();
    }
  }, [timeoutRef, shouldShowSignUpModal, openSignUpModal, finishProcess]);

  useEffect(() => {
    if (state === StatusModalStates.SUCCESS && !shouldShowDeals) {
      onFinishProcess();
    }
  }, [state, timeoutRef, onFinishProcess, shouldShowDeals]);

  const {successAnimationIcon} = useCustomLotties();
  const onSignUp = () => {
    closeSignUpModal();
    navigate(getFullPath(PATHS.auth.createAccount));
  };

  const onKeepEnteringDataManually = () => {
    closeSignUpModal();
    finishProcess();
  };

  return (
    <>
      <StyledStatusModal
        open={open}
        state={state}
        maxWidth={isIVOnPropertyUnavailable ? '541px' : '375px'}
        closeOnDocumentClick={false}
      >
        {state === StatusModalStates.SUCCESS && !shouldShowDeals ? (
          <ModalGuestRegisteredNextStepContent closeModal={onClose} />
        ) : (
          <GuestsProcessingModal
            onClose={onClose}
            title={t(title)}
            state={state}
            errorMessage={errorMessage}
            hasOffers={shouldShowDeals}
            onFinishProcess={onFinishProcess}
            onNavigateToCart={handleNavigateToCart}
            successSubtitle={
              <Trans
                i18nKey={
                  isEdition
                    ? 'guest_updated_modal_description'
                    : 'guest_registered_modal_description'
                }
                values={{name}}
              />
            }
          />
        )}
      </StyledStatusModal>

      <StyledModalSheet
        isOpenModalSheet={isOpenNextStepModal}
        handleCloseModal={closeNextStepModal}
        closeOnDocumentClick={false}
        withModalMobileCloseButton={false}
        closeButtonType="none"
        className="kiosko:py-15 kiosko:px-16"
        iconLottie={successAnimationIcon}
        $hasInfoBox={isIVOnPropertyUnavailable}
      >
        <ModalGuestRegisteredNextStepContent closeModal={closeNextStepModal} />
      </StyledModalSheet>

      <StyledModalSheet
        isOpenModalSheet={isOpenSignUpModal}
        handleCloseModal={closeSignUpModal}
        closeOnDocumentClick={false}
        withModalMobileCloseButton={false}
        closeButtonType="none"
      >
        <SignUpSuggestionModal
          onKeepEnteringDataManually={onKeepEnteringDataManually}
          onSignUp={onSignUp}
        />
      </StyledModalSheet>
    </>
  );
}

export {GuestRegisteringModal};
